import type { Control, FieldValues, FieldPath } from 'react-hook-form'

/**
 * Base props for react-hook-form integration.
 * Include this in your RHF props type definition.
 */
export interface RHFProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  /**
   * Field name for react-hook-form.
   */
  name: TName

  /**
   * Control object from useForm.
   */
  control: Control<TFieldValues>
}

/**
 * Type guard to check if props are for RHF (react-hook-form) mode.
 * Returns true if props contain a `control` property, indicating RHF usage.
 *
 * @example
 * ```tsx
 * type MyProps<T extends FieldValues, N extends FieldPath<T>> =
 *   | StandaloneProps
 *   | (StandaloneProps & RHFProps<T, N>)
 *
 * function MyComponent(props: MyProps<T, N>) {
 *   if (isRHFProps(props)) {
 *     // props.name and props.control are available
 *     const { field } = useController({ name: props.name, control: props.control })
 *   }
 * }
 * ```
 */
export function isRHFProps<
  TProps extends object,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: TProps
): props is TProps & RHFProps<TFieldValues, TName> {
  return 'control' in props && props.control !== undefined
}
